import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SITE from '../api';
import { useSite, IMG, SocialIcons } from '../components/StoreLayout';
import useSEO from '../useSEO';
import { STORE_BASE } from '../routeBase';

function ytVideoId(url) {
  if (!url) return '';
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([a-zA-Z0-9_-]{6,})/);
  return m ? m[1] : '';
}

export default function AboutPage() {
  const { content: c, brands } = useSite();
  const [team, setTeam] = useState([]);
  const [testimonials, setTestimonials] = useState([]);

  useSEO({
    title: c['about.meta_title'] || c['about.title'] || 'About Us',
    description: c['about.meta_description'] || `${c['brand.name'] || 'Salvador Eyewear'} (Prakaash Eyewear Pvt. Ltd.) is a wholesale eyewear manufacturer and exporter based in Mumbai, India, in business since 1978.`,
    keywords: c['about.meta_keywords'] || 'eyewear manufacturer India, wholesale eyeglass frames, Salvador Eyewear, Prakaash Eyewear',
    canonical: `${window.location.origin}/store/about`,
  });

  useEffect(() => {
    SITE.get('/team-members').then(r => setTeam(r.data.members || []));
    SITE.get('/testimonials').then(r => setTestimonials(r.data.testimonials || []));
  }, []);

  return (
    <div>
      <style>{`
        .sf-2col { display:grid; gap:40px; grid-template-columns:1fr 1fr; }
        .sf-2col-brands { display:grid; gap:40px; grid-template-columns:1fr 1.4fr; }
        @media (max-width:700px) {
          .sf-2col, .sf-2col-brands { grid-template-columns:1fr; }
        }
      `}</style>

      {/* Banner — aspect-ratio matches the 1600x500 "best size" hint in the
          admin editor, so a fluid-width box doesn't drift from the uploaded
          image's shape and force background-size:cover to crop it. minHeight
          stays as a floor for narrow/mobile screens. */}
      <div style={{
        position: 'relative', minHeight: 220, display: 'flex', alignItems: 'center', justifyContent: 'center',
        textAlign: 'center', background: c['about.image'] ? `url(${IMG(c['about.image'])}) center/cover` : '#232323',
        ...(c['about.image'] ? { aspectRatio: '1600 / 500' } : {}),
      }}>
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)' }} />
        <div style={{ position: 'relative', color: '#fff', padding: '24px' }}>
          <h1 style={{ fontSize: 32, fontWeight: 700, margin: 0 }}>{c['about.title'] || 'About Us'}</h1>
          {c['about.banner_subtitle'] && <div style={{ fontSize: 15, marginTop: 8, opacity: 0.9 }}>{c['about.banner_subtitle']}</div>}
        </div>
      </div>

      {/* Vision / Mission */}
      {(c['about.vision_text'] || c['about.mission_text']) && (
        <section style={{ maxWidth: 1100, margin: '0 auto', padding: '40px 24px 10px' }}>
          <div className="sf-2col">
            {c['about.vision_text'] && (
              <div>
                <h2 style={sectionTitle}>Our Vision</h2>
                <div className="sf-richtext" style={proseStyle} dangerouslySetInnerHTML={{ __html: c['about.vision_text'] }} />
              </div>
            )}
            {c['about.mission_text'] && (
              <div>
                <h2 style={sectionTitle}>Our Mission</h2>
                <div className="sf-richtext" style={proseStyle} dangerouslySetInnerHTML={{ __html: c['about.mission_text'] }} />
              </div>
            )}
          </div>
        </section>
      )}

      {/* Who We Are */}
      {(c['about.whoweare_body'] || c['about.whoweare_image_1'] || c['about.whoweare_image_2']) && (
        <section style={{ background: '#f7f8fa', padding: '48px 0', marginTop: 30 }}>
          <div className="sf-2col" style={{ maxWidth: 1100, margin: '0 auto', padding: '0 24px', alignItems: 'center' }}>
            <div>
              <h2 style={sectionTitle}>Who We Are</h2>
              {c['about.whoweare_lead'] && <p style={{ fontSize: 15.5, fontWeight: 500, color: 'var(--sf-primary)', margin: '0 0 12px' }}>{c['about.whoweare_lead']}</p>}
              <div className="sf-richtext" style={proseStyle} dangerouslySetInnerHTML={{ __html: c['about.whoweare_body'] || '' }} />
              <SocialIcons c={c} size={36} />
            </div>
            <div style={{ position: 'relative', minHeight: 280 }}>
              {c['about.whoweare_image_2'] && (
                <img src={IMG(c['about.whoweare_image_2'])} alt="" style={{ position: 'absolute', right: 0, top: 30, width: '75%', borderRadius: 10, boxShadow: '0 10px 30px rgba(0,0,0,0.12)' }} />
              )}
              {c['about.whoweare_image_1'] && (
                <img src={IMG(c['about.whoweare_image_1'])} alt="" style={{ position: 'absolute', left: 0, bottom: 0, width: '65%', borderRadius: 10, boxShadow: '0 10px 30px rgba(0,0,0,0.16)', border: '5px solid #fff' }} />
              )}
              {!c['about.whoweare_image_1'] && !c['about.whoweare_image_2'] && (
                <div style={{ height: 260, borderRadius: 10, background: '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#aaa', fontSize: 13 }}>Images coming soon</div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Brands */}
      {brands?.length > 0 && (
        <section style={{ maxWidth: 1100, margin: '0 auto', padding: '48px 24px' }}>
          <div className="sf-2col-brands" style={{ alignItems: 'center' }}>
            <div>
              <h2 style={sectionTitle}>{c['about.brands_title'] || "The World's Premium Eyewear Brands"}</h2>
              {c['about.brands_text'] && <p style={proseStyle}>{c['about.brands_text']}</p>}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 16 }}>
              {brands.map(b => (
                <Link key={b.id} to={`${STORE_BASE}/shop?brand=${encodeURIComponent(b.name)}`} title={`Shop ${b.name}`} className="sf-brand-tile"
                  style={{ height: 70, border: '1px solid #eee', borderRadius: 8, background: '#fafafa', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 8, textDecoration: 'none' }}>
                  {b.logo
                    ? <img src={IMG(b.logo)} alt={b.name} loading="lazy" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                    : <span style={{ fontSize: 10, color: '#999', textAlign: 'center' }}>{b.name}</span>}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Meet Our Team */}
      {team.length > 0 && (
        <section style={{ maxWidth: 1100, margin: '0 auto', padding: '10px 24px 48px' }}>
          <h2 style={{ ...sectionTitle, textAlign: 'center', marginBottom: 28 }}>{c['about.team_title'] || 'Meet Our Team'}</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 24 }}>
            {team.map(m => <TeamCard key={m.id} m={m} />)}
          </div>
        </section>
      )}

      {/* Manufacturing Skills */}
      {(c['about.skills_media'] || c['about.skills_youtube_url'] || c['about.skill_metal']) && (
        <section style={{ background: '#f7f8fa', padding: '48px 0' }}>
          <div className="sf-2col" style={{ maxWidth: 1100, margin: '0 auto', padding: '0 24px', alignItems: 'center' }}>
            <div style={{ borderRadius: 10, overflow: 'hidden', background: '#eee', minHeight: 220, aspectRatio: c['about.skills_youtube_url'] ? '16/9' : undefined, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {ytVideoId(c['about.skills_youtube_url']) ? (
                <iframe
                  key={c['about.skills_youtube_url']}
                  src={`https://www.youtube.com/embed/${ytVideoId(c['about.skills_youtube_url'])}?autoplay=0&loop=1&playlist=${ytVideoId(c['about.skills_youtube_url'])}&controls=1&rel=0&modestbranding=1`}
                  title="Manufacturing skills video"
                  style={{ width: '100%', height: '100%', border: 0, display: 'block' }}
                  allow="accelerometer; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : c['about.skills_media']
                ? <img src={IMG(c['about.skills_media'])} alt="" loading="lazy" style={{ width: '100%', display: 'block' }} />
                : <span style={{ color: '#aaa', fontSize: 13 }}>Image coming soon</span>}
            </div>
            <div>
              <h2 style={sectionTitle}>{c['about.skills_title'] || 'Our Manufacturing Excellence'}</h2>
              <SkillBar label="Metal Optical Frames" pct={c['about.skill_metal']} />
              <SkillBar label="Acetate Frames" pct={c['about.skill_acetate']} />
              <SkillBar label="TR90 Frames" pct={c['about.skill_tr90']} />
              <SkillBar label="Sunglasses" pct={c['about.skill_sunglasses']} />
            </div>
          </div>
        </section>
      )}

      {/* Testimonials — auto-scrolling strip */}
      {testimonials.length > 0 && (
        <section style={{ background: '#fff', borderTop: '1px solid #eee', padding: '48px 0', overflow: 'hidden' }}>
          <h2 style={{ ...sectionTitle, textAlign: 'center', marginBottom: 28 }}>{c['about.testimonials_title'] || 'What Customers Say About Us'}</h2>
          <TestimonialScroller items={testimonials} />
        </section>
      )}
    </div>
  );
}

const sectionTitle = { fontSize: 22, fontWeight: 700, margin: '0 0 14px', color: '#191919' };
const proseStyle = { fontSize: 14.5, lineHeight: 1.75, color: '#555' };
function TeamCard({ m }) {
  const [hover, setHover] = useState(false);
  const socials = [
    ['facebook', m.facebook, 'ti-brand-facebook'],
    ['twitter', m.twitter, 'ti-brand-twitter'],
    ['instagram', m.instagram, 'ti-brand-instagram'],
    ['linkedin', m.linkedin, 'ti-brand-linkedin'],
  ].filter(([, url]) => url);

  return (
    <div style={{ textAlign: 'center' }} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      {/* aspectRatio (not a fixed height) keeps this box genuinely square —
          the grid column it sits in (minmax(220px, 1fr)) can stretch wider
          than 220px, so a fixed height would let cover crop the "square,
          e.g. 400x400" photo the admin hint asks for. */}
      <div style={{ position: 'relative', borderRadius: 10, overflow: 'hidden', aspectRatio: '1 / 1', background: '#fafafa' }}>
        {m.photo
          ? <img src={IMG(m.photo)} alt={m.name} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#bbb', fontSize: 12 }}>No photo</div>}
        {(m.bio || socials.length > 0) && (
          <div style={{
            position: 'absolute', inset: 0, background: 'rgba(25,25,25,0.75)', display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: 10, opacity: hover ? 1 : 0, transition: 'opacity .2s',
          }}>
            {m.bio && <p style={{ color: '#fff', fontSize: 11.5, padding: '0 16px', margin: 0 }}>{m.bio}</p>}
            {socials.length > 0 && (
              <div style={{ display: 'flex', gap: 10 }}>
                {socials.map(([key, url, icon]) => (
                  <a key={key} href={url} target="_blank" rel="noreferrer" style={{ color: '#fff', fontSize: 15 }}><i className={`ti ${icon}`} /></a>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      <div style={{ fontWeight: 700, fontSize: 15, marginTop: 12 }}>{m.name}</div>
      {m.designation && <div style={{ fontSize: 12, color: 'var(--sf-primary)' }}>{m.designation}</div>}
    </div>
  );
}

function SkillBar({ label, pct }) {
  const n = Math.max(0, Math.min(100, parseInt(pct, 10) || 0));
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
        <span>{label}</span><span>{n}%</span>
      </div>
      <div style={{ height: 8, borderRadius: 4, background: '#e6e6e6', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${n}%`, background: 'var(--sf-primary)', borderRadius: 4, transition: 'width 1s ease' }} />
      </div>
    </div>
  );
}

// Continuous auto-scrolling strip — CSS keyframe animation (no timers), the
// item list is duplicated once so the loop wraps seamlessly; pauses on hover.
export function TestimonialScroller({ items }) {
  const track = [...items, ...items];
  return (
    <div style={{ overflow: 'hidden' }}>
      <style>{`
        @keyframes sf-testimonial-scroll { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        .sf-testimonial-track { display: flex; gap: 20px; width: max-content; animation: sf-testimonial-scroll ${Math.max(items.length * 8, 16)}s linear infinite; }
        .sf-testimonial-track:hover { animation-play-state: paused; }
      `}</style>
      <div className="sf-testimonial-track">
        {track.map((t, i) => (
          <div key={`${t.id}-${i}`} style={{
            width: 340, flexShrink: 0, background: '#f7f8fa', borderRadius: 12, padding: '24px 22px', textAlign: 'center',
          }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', overflow: 'hidden', margin: '0 auto 14px', background: '#eee' }}>
              {t.photo
                ? <img src={IMG(t.photo)} alt={t.customer_name} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#bbb', fontSize: 18 }}>{t.customer_name?.[0]}</div>}
            </div>
            <p style={{ fontSize: 13, fontStyle: 'italic', color: '#555', lineHeight: 1.6, margin: '0 0 14px' }}>&ldquo;{t.quote}&rdquo;</p>
            <div style={{ fontWeight: 700, fontSize: 13.5 }}>{t.customer_name}</div>
            {t.designation && <div style={{ fontSize: 11.5, color: '#999' }}>{t.designation}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}
